// src/components/Admin/ItemManager/ItemTable.jsx
import {
  Table, TableHead, TableRow, TableCell, TableBody,
  IconButton, Chip, Box, Pagination, TextField, Select, MenuItem,
  Stack, Typography, useTheme, useMediaQuery,
  Paper, Divider, Button,
} from '@mui/material';
import { Edit, Delete } from '@mui/icons-material';
import VisibilityIcon from '@mui/icons-material/Visibility';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
import { useState, useMemo, useEffect } from 'react';

const getFullImageUrl = (url) =>
  url?.startsWith('http') ? url : `https://sandle.it.com${url}`;

const rowsPerPage = 5;

const formatStock = (it) => {
  if (it.is_unlimited) return '무제한';
  return `${it.remaining ?? 0} / ${it.stock ?? 0}`;
};

export default function ItemTable({ items = [], onEdit, onDelete, onToggleActive }) {
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState('');
  const [filter, setFilter] = useState('all');

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const filteredItems = useMemo(() => (
    items
      .filter((it) => {
        if (filter === 'active') return !!it.is_active;
        if (filter === 'inactive') return !it.is_active;
        return true;
      })
      .filter((it) =>
        !search.trim() || (it.name || '').toLowerCase().includes(search.toLowerCase()))
  ), [items, search, filter]);

  const totalPages = Math.max(1, Math.ceil(filteredItems.length / rowsPerPage));

  useEffect(() => {
    if (page > totalPages) setPage(totalPages);
  }, [page, totalPages]);

  const paginatedItems = useMemo(() => (
    filteredItems.slice((page - 1) * rowsPerPage, page * rowsPerPage)
  ), [filteredItems, page]);

  const handleChangePage = (_, v) => setPage(v);
  const handleSearchChange = (e) => { setSearch(e.target.value); setPage(1); };
  const handleFilterChange = (e) => { setFilter(e.target.value); setPage(1); };

  return (
    <Box
      sx={{
        // ✅ 모바일 폭 안맞는 원인이었던 scale 제거
        width: '100%',
      }}
    >
      {/* 검색/필터 */}
      <Stack
        direction={isMobile ? 'column' : 'row'}
        spacing={2}
        sx={{
          mb: 2,
          width: '100%',            // ✅ 등록 버튼과 동일한 폭
        }}
        flexWrap="wrap"
        alignItems="center"
      >
        <TextField
          label="아이템명 검색"
          size="small"
          value={search}
          onChange={handleSearchChange}
          fullWidth={isMobile}
          sx={isMobile ? { width: '100%' } : {}}
        />
        <Select
          size="small"
          value={filter}
          onChange={handleFilterChange}
          fullWidth={isMobile}
          sx={isMobile ? { width: '100%' } : {}}
        >
          <MenuItem value="all">전체</MenuItem>
          <MenuItem value="active">노출 중</MenuItem>
          <MenuItem value="inactive">숨김</MenuItem>
        </Select>

        <Typography
          variant="body2"
          sx={{
            ml: isMobile ? 0 : 'auto',
            mt: isMobile ? 1 : 0,
            textAlign: isMobile ? 'right' : 'left',
            width: isMobile ? '100%' : 'auto', // ✅ 모바일에선 바 폭 맞춰서 우측정렬
          }}
        >
          총 {filteredItems.length}건
        </Typography>
      </Stack>

      {/* ✅ 모바일 = 카드형 */}
      {isMobile ? (
        <Stack spacing={1.25} sx={{ width: '100%' }}>
          {paginatedItems.length === 0 ? (
            <Paper variant="outlined" sx={{ p: 2, textAlign: 'center', width: '100%' }}>
              <Typography sx={{ color: 'text.secondary' }}>
                🔍 조건에 맞는 아이템이 없습니다.
              </Typography>
            </Paper>
          ) : (
            paginatedItems.map((it) => (
              <Paper
                key={it.id}
                variant="outlined"
                sx={{ p: 1.5, borderRadius: 2, width: '100%' }}
              >
                {/* 상단: 이미지 + 이름 + 칩 */}
                <Box sx={{ display: 'flex', gap: 1.25, alignItems: 'center', minWidth: 0 }}>
                  <Box sx={{ flex: 'none' }}>
                    {it.image ? (
                      <img
                        src={getFullImageUrl(it.image)}
                        alt={it.name}
                        width={44}
                        height={44}
                        style={{ borderRadius: 8, objectFit: 'cover' }}
                      />
                    ) : (
                      <Box
                        sx={{
                          width: 44,
                          height: 44,
                          borderRadius: 2,
                          bgcolor: 'grey.100',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          color: 'text.secondary',
                          fontSize: 12,
                        }}
                      >
                        —
                      </Box>
                    )}
                  </Box>

                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography
                      sx={{
                        fontWeight: 800,
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                      }}
                    >
                      {it.name}
                    </Typography>

                    <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mt: 0.6 }}>
                      <Chip size="small" variant="outlined" label={it.is_coupon ? '쿠폰' : '일반'} />
                      <Chip
                        label={it.is_active ? '노출 중' : '숨김'}
                        color={it.is_active ? 'success' : 'default'}
                        size="small"
                        icon={it.is_active ? <VisibilityIcon /> : <VisibilityOffIcon />}
                        onClick={() => onToggleActive(it.id, it.is_active)}
                        clickable
                      />
                    </Box>
                  </Box>
                </Box>

                <Divider sx={{ my: 1.25 }} />

                {/* 정보 */}
                <Stack spacing={0.75}>
                  <Stack direction="row" justifyContent="space-between" alignItems="center">
                    <Typography variant="body2" color="text.secondary">가격</Typography>
                    <Typography variant="body2" sx={{ fontWeight: 800 }}>
                      {it.price ?? 0} Leaf🌿
                    </Typography>
                  </Stack>

                  <Stack direction="row" justifyContent="space-between" alignItems="center">
                    <Typography variant="body2" color="text.secondary">재고</Typography>
                    <Typography variant="body2" sx={{ fontWeight: 700 }}>
                      {formatStock(it)}
                    </Typography>
                  </Stack>
                </Stack>

                {/* 버튼형 수정/삭제 */}
                <Stack direction="row" spacing={1} sx={{ mt: 1.25 }}>
                  <Button
                    size="small"
                    variant="outlined"
                    fullWidth
                    onClick={() => onEdit(it)}
                  >
                    수정
                  </Button>
                  <Button
                    size="small"
                    variant="outlined"
                    color="error"
                    fullWidth
                    onClick={() => onDelete(it.id)}
                  >
                    삭제
                  </Button>
                </Stack>
              </Paper>
            ))
          )}

          {totalPages > 1 && (
            <Box mt={0.5} display="flex" justifyContent="center">
              <Pagination
                count={totalPages}
                page={page}
                onChange={handleChangePage}
                color="primary"
                showFirstButton
                showLastButton
                size="small"
                shape="rounded"
              />
            </Box>
          )}
        </Stack>
      ) : (
        /* ✅ PC = 테이블 그대로 */
        <>
          <Box>
            <Table size="small" stickyHeader>
              <TableHead>
                <TableRow>
                  <TableCell>미리보기</TableCell>
                  <TableCell>이름</TableCell>
                  <TableCell>가격</TableCell>
                  <TableCell>재고</TableCell>
                  <TableCell>타입</TableCell>
                  <TableCell>노출 상태</TableCell>
                  <TableCell>작업</TableCell>
                </TableRow>
              </TableHead>

              <TableBody>
                {paginatedItems.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={7} align="center" sx={{ py: 5 }}>
                      🔍 조건에 맞는 아이템이 없습니다.
                    </TableCell>
                  </TableRow>
                ) : (
                  paginatedItems.map((it) => (
                    <TableRow key={it.id}>
                      <TableCell align="center">
                        {it.image ? (
                          <img
                            src={getFullImageUrl(it.image)}
                            alt={it.name}
                            width={36}
                            height={36}
                            style={{ borderRadius: 4, objectFit: 'cover' }}
                          />
                        ) : '—'}
                      </TableCell>

                      <TableCell>{it.name}</TableCell>
                      <TableCell>{it.price} Leaf🌿</TableCell>
                      <TableCell>{formatStock(it)}</TableCell>
                      <TableCell>{it.is_coupon ? '쿠폰' : '일반'}</TableCell>

                      <TableCell>
                        <Chip
                          label={it.is_active ? '노출 중' : '숨김'}
                          color={it.is_active ? 'success' : 'default'}
                          size="small"
                          icon={it.is_active ? <VisibilityIcon /> : <VisibilityOffIcon />}
                          onClick={() => onToggleActive(it.id, it.is_active)}
                          clickable
                        />
                      </TableCell>

                      <TableCell>
                        <IconButton onClick={() => onEdit(it)}>
                          <Edit fontSize="small" />
                        </IconButton>
                        <IconButton onClick={() => onDelete(it.id)}>
                          <Delete fontSize="small" />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </Box>

          {totalPages > 1 && (
            <Box mt={2} display="flex" justifyContent="center">
              <Pagination
                count={totalPages}
                page={page}
                onChange={handleChangePage}
                color="primary"
                showFirstButton
                showLastButton
              />
            </Box>
          )}
        </>
      )}
    </Box>
  );
}
